import { useReviews, usePublishedReviews } from "@/hooks/use-reviews";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { Star, MessageSquare } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

export function ReviewList() {
  const { data: reviews, isLoading } = useReviews();
  const { data: allReviews } = usePublishedReviews();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 rounded-2xl w-full" />
        ))}
      </div>
    );
  }

  if (!reviews?.length) return null;

  // Rating summary reflects ALL published/approved reviews, while the
  // carousel below shows only the curated highlighted subset.
  const summaryReviews = allReviews && allReviews.length > 0 ? allReviews : reviews;
  const totalReviews = summaryReviews.length;
  const avgRating = summaryReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / totalReviews;

  const renderStars = (rating: number, size: string = "h-4 w-4") => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Rating summary */}
      <div className="flex items-center justify-center gap-3">
        <div className="text-4xl font-bold">{avgRating.toFixed(1)}</div>
        <div className="space-y-1">
          {renderStars(Math.round(avgRating), "h-5 w-5")}
          <p className="text-xs text-muted-foreground">
            {totalReviews} {totalReviews === 1 ? "review" : "reviews"}
          </p>
        </div>
      </div>

      <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full max-w-6xl mx-auto px-4"
      >
        <CarouselContent className="-ml-2 mr-2">
          {reviews.map((review: any) => (
            <CarouselItem key={review.id} className="md:basis-1/2 lg:basis-1/3 p-2">
              <div className="bg-card h-full p-6 rounded-2xl border border-border shadow-sm flex flex-col gap-3">
                {/* Stars */}
                {renderStars(review.rating || 0)}

                {/* Comment */}
                {review.comment ? (
                  <p className="text-muted-foreground text-sm leading-relaxed flex-1 overflow-hidden line-clamp-4">
                    "{review.comment}"
                  </p>
                ) : (
                  <p className="text-muted-foreground text-sm italic flex-1">
                    <MessageSquare className="inline h-3 w-3 me-1" />
                    No comment
                  </p>
                )}

                {/* Customer info */}
                <div className="flex items-center gap-2 pt-2 border-t border-border/40">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-primary">
                      {(review.customerName || review.maskedPhone || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {review.customerName || "Anonymous"}
                    </p>
                    {review.maskedPhone && (
                      <p className="text-xs text-muted-foreground">{review.maskedPhone}</p>
                    )}
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex justify-center gap-4 mt-8">
          {isRTL ? (
            <>
              <CarouselNext className="static translate-y-0" />
              <CarouselPrevious className="static translate-y-0" />
            </>
          ) : (
            <>
              <CarouselPrevious className="static translate-y-0" />
              <CarouselNext className="static translate-y-0" />
            </>
          )}
        </div>
      </Carousel>
    </div>
  );
}
